import { motion } from 'framer-motion';
import {
  Calendar,
  Users,
  CheckCircle,
  ArrowRight,
  TrendingUp,
  AlertCircle,
  Target,
  Zap,
  Clock,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function StrategicCadencePage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const cadenceLevels = [
    {
      level: 'Annual Strategic Planning',
      icon: Calendar,
      frequency: 'Once per year (typically Q4 for next year)',
      duration: '2-3 days offsite',
      participants: 'Executive team + key leaders',
      purpose: 'Set direction for next 12+ months',
      agenda: [
        'Review previous year performance',
        'Market and competitive landscape analysis',
        'Update vision, mission, strategic objectives',
        'Set annual OKRs and initiatives',
        'Allocate budget and resources',
        'Identify key risks and mitigation plans',
      ],
      outputs: [
        'Annual strategic plan document',
        'Company-wide OKRs',
        'Strategic initiative portfolio',
        'Budget allocations',
        'Communication plan',
      ],
      tip: "Start planning 3 months before year-end. Don't wait until December. Best months: September-October for deep analysis, November for decisions.",
    },
    {
      level: 'Quarterly Business Reviews (QBRs)',
      icon: Users,
      frequency: 'Every 3 months',
      duration: '4-6 hours',
      participants: 'Full leadership team',
      purpose: 'Assess progress, adjust course, set next quarter priorities',
      agenda: [
        "Score previous quarter OKRs (what worked, what didn't)",
        'Review KPI performance vs. targets',
        'Initiative progress: continue/pivot/kill decisions',
        'Market/competitive updates',
        'Set next quarter OKRs',
        'Resource reallocation as needed',
      ],
      outputs: [
        'Quarterly OKR scores and retrospective',
        'Next quarter OKRs',
        'Initiative status updates',
        'Course corrections',
        'All-hands communication',
      ],
      tip: "Schedule QBRs on week 2 of new quarter (week 1 of month 1). Gives 1 week for data collection but starts quickly. Don't wait until month 2.",
    },
    {
      level: 'Monthly Leadership Meetings',
      icon: CheckCircle,
      frequency: 'Every month',
      duration: '2-3 hours',
      participants: 'Department heads',
      purpose: 'Tactical coordination and issue resolution',
      agenda: [
        'Progress on quarterly OKRs (confidence scoring)',
        'Key metrics review (KPI dashboard)',
        'Cross-functional dependencies and blockers',
        'Resource needs and constraints',
        'Upcoming initiatives and milestones',
        'Team health and capacity',
      ],
      outputs: [
        'Action items with owners',
        'Escalations to executive team',
        'Resource requests',
        'Risk register updates',
        'Team communications',
      ],
      tip: 'Hold on same day/time every month (e.g., first Tuesday). Consistency builds rhythm. 2-hour hard stop forces prioritization.',
    },
    {
      level: 'Weekly Execution Check-ins',
      icon: Zap,
      frequency: 'Every week',
      duration: '30-60 minutes',
      participants: 'Team leads + direct reports',
      purpose: 'Operational pulse check and rapid problem-solving',
      agenda: [
        'Update on weekly commitments from last meeting',
        'Progress on current sprint/initiatives',
        'Blockers and escalations',
        'Week ahead priorities',
        'Quick wins and celebrations',
        'Help needed',
      ],
      outputs: [
        'Weekly priorities list',
        'Blocker resolutions',
        'Next week commitments',
        'Escalations captured',
        'Team alignment',
      ],
      tip: "Keep to 30 min max. Use standup format: What did we accomplish? What's next? What's blocking us? No problem-solving in meeting—take offline.",
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Execution & Monitoring', path: '/execution-monitoring' },
              { label: 'Strategic Cadence' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <TrendingUp className="w-4 h-4 mr-2" />
            Execution & Monitoring Tool 2 of 4
          </span>
        }
        title="Keep your strategy in the conversation"
        subtitle="Build a rhythm for reviewing progress, making decisions, and following through. Connect annual direction with quarterly priorities and the work happening each week."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Set your review rhythm
        </Link>
      </PageHero>

      {/* The Four-Level Cadence Model */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">Give each review a clear job to do</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Strategy stays useful when the team returns to it. Set a cadence where annual,
              quarterly, monthly, and weekly reviews each serve a distinct purpose.
            </p>
          </motion.div>
          <div className="max-w-6xl mx-auto space-y-8">
            {cadenceLevels.map((level, idx) => (
              <motion.div
                key={idx}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-600 p-3 rounded-lg">
                      <level.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">{level.level}</h3>
                      <p className="text-sm text-blue-700 font-medium">
                        {level.frequency} • {level.duration}
                      </p>
                    </div>
                  </div>
                  <div className="bg-blue-100 px-4 py-2 rounded-lg">
                    <p className="text-xs text-blue-700 font-semibold">{level.participants}</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-6 text-lg font-medium">{level.purpose}</p>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-gray-900 mb-3">Meeting Agenda:</p>
                    <ul className="space-y-2">
                      {level.agenda.map((item, i) => (
                        <li key={i} className="text-xs text-gray-700 flex items-start">
                          <CheckCircle className="w-3 h-3 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-gray-900 mb-3">Key Outputs:</p>
                    <ul className="space-y-2">
                      {level.outputs.map((output, i) => (
                        <li key={i} className="text-xs text-gray-700 flex items-start">
                          <Target className="w-3 h-3 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                          {output}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-blue-700">Pro Tip:</span> {level.tip}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Implement */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">How to Implement Strategic Cadence</h2>
              <p className="text-xl text-gray-600">Six steps to establish rhythm that sticks</p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Block Dates for Entire Year',
                  description:
                    'First action: Put all annual, quarterly, monthly meetings on calendar NOW. Annual planning in Q4, QBRs in weeks 2 of Q1/Q2/Q3/Q4, monthly on first Tuesday. Treat as non-negotiable.',
                  tip: 'Send calendar invites 12 months out. Sounds extreme but prevents scheduling conflicts. Much easier to move one meeting than to find time for 12 people quarterly.',
                },
                {
                  step: '2',
                  title: 'Create Standard Meeting Templates',
                  description:
                    "Don't reinvent agenda each time. Build templates for each cadence level with: purpose, agenda, pre-work, expected outputs, time allocations. Consistency = efficiency.",
                  tip: 'Use "read-ahead" culture. Send pre-reads 48 hours before. Use meeting time for discussion and decisions, not presentations. 10-page memos > 40-slide decks.',
                },
                {
                  step: '3',
                  title: 'Assign Meeting Owners and Roles',
                  description:
                    'Each meeting type needs owner (plans agenda, runs meeting, captures decisions). Rotating facilitator ≠ owner. Owner provides continuity across cycles.',
                  tip: 'Use "scribe + timekeeper + facilitator" roles. Scribe captures decisions/actions. Timekeeper keeps on schedule. Facilitator runs discussion. Owner orchestrates all.',
                },
                {
                  step: '4',
                  title: 'Link Each Level to Next',
                  description:
                    'Annual planning → Q1 OKRs. Q1 review → month 4 priorities. Monthly → weekly commitments. Every meeting must connect to level above and cascade to level below.',
                  tip: 'End every meeting with: "Based on this discussion, what changes for next level down?" Annual changes quarterly. Quarterly changes monthly. Monthly changes weekly.',
                },
                {
                  step: '5',
                  title: 'Establish Decision-Making Protocols',
                  description:
                    'For each cadence level, define: What decisions are made here? Who has final say? What needs escalation? Clear decision rights prevent endless "we\'ll discuss more next time."',
                  tip: 'Use DACI framework: Driver (leads decision), Approver (final say), Contributors (input), Informed (told after). Assign at start of meeting for key decisions.',
                },
                {
                  step: '6',
                  title: 'Start Small, Build Discipline',
                  description:
                    "Don't launch all four levels at once. Start with quarterly + monthly. Once those have rhythm (3+ cycles), add weekly. Annual planning comes naturally once quarterly is working.",
                  tip: "First 3 cycles are learning. Expect awkwardness. Retrospective after each: What worked? What didn't? Adjust format. By cycle 4, should feel natural.",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {item.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sample Annual Calendar */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Sample Strategic Cadence Calendar</h2>
              <p className="text-xl text-gray-600">
                Visualize how rhythms nest throughout the year
              </p>
            </motion.div>
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-card bg-white p-8 rounded-xl border border-gray-200"
            >
              <div className="grid grid-cols-4 gap-4 mb-6">
                {['Q1', 'Q2', 'Q3', 'Q4'].map((q, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-lg border border-blue-300">
                    <h3 className="text-xl font-bold text-blue-600 mb-3">{q}</h3>
                    <div className="space-y-2 text-xs">
                      <div className="bg-purple-100 p-2 rounded border-l-4 border-purple-600">
                        <p className="font-bold">Week 2: QBR</p>
                        <p className="text-gray-600">6-hour review</p>
                      </div>
                      {[1, 2, 3].map((m) => (
                        <div
                          key={m}
                          className="bg-green-100 p-2 rounded border-l-4 border-green-600"
                        >
                          <p className="font-bold">Month {m}: Leadership</p>
                          <p className="text-gray-600">2-hour meeting</p>
                        </div>
                      ))}
                      <div className="bg-blue-100 p-2 rounded">
                        <p className="font-bold text-blue-700">13 Weekly Check-ins</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-orange-100 p-6 rounded-lg border border-orange-400">
                <div className="flex items-center space-x-3 mb-2">
                  <Clock className="w-6 h-6 text-orange-600" />
                  <h3 className="text-xl font-bold">Q4 Special: Annual Strategic Planning</h3>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  2-3 day offsite in October/November to set next year direction
                </p>
                <p className="text-xs text-gray-600">
                  Executive team + department heads • Strategy refresh + budget + OKRs
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common Strategic Cadence Mistakes</h2>
              <p className="text-xl text-gray-600">
                Pitfalls that break rhythm and undermine execution
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Canceling or Rescheduling Regularly',
                  description:
                    'Allowing "urgent" work to displace strategic meetings. Once you skip a QBR or monthly meeting, cadence breaks. Team learns strategy isn\'t really priority.',
                  fix: "Treat cadence meetings as sacrosanct. Same level as customer meetings or board meetings. If CEO can't miss board meeting, can't miss QBR. Period.",
                },
                {
                  mistake: 'No Pre-Work or Preparation',
                  description:
                    'People show up without reading materials, reviewing data, or thinking through issues. Meeting becomes status report, not strategic discussion. Waste of time.',
                  fix: 'Enforce read-ahead culture. Materials due 48 hours before. Quiz attendees: "What stood out in the pre-read?" If unprepared, reschedule and hold people accountable.',
                },
                {
                  mistake: 'Same Meeting at All Levels',
                  description:
                    'Using quarterly meeting agenda for monthly meetings, or annual planning format for QBRs. Each level has different purpose—needs different structure.',
                  fix: 'Unique agenda per cadence level. Annual = strategic direction. Quarterly = adjust course. Monthly = tactical coordination. Weekly = operational pulse. Keep distinct.',
                },
                {
                  mistake: 'No Follow-Through on Decisions',
                  description:
                    'Making decisions in meetings, then nothing happens. No action items captured, no owners assigned, no follow-up. Decisions evaporate.',
                  fix: 'End every meeting with review of decisions made + action items + owners + deadlines. Send summary email within 24 hours. Track completion at next meeting.',
                },
                {
                  mistake: 'Death by PowerPoint',
                  description:
                    'Spending 80% of meeting time on presentations, 20% on discussion. Leaders become audience instead of decision-makers. Frustrating and unproductive.',
                  fix: 'Amazon-style: Written memos distributed 48 hours before. First 15 min of meeting = silent reading if needed. Remaining time = discussion only. No slides.',
                },
                {
                  mistake: 'Not Cascading Decisions',
                  description:
                    "Leaders make decisions in QBR, but teams below don't hear about them until weeks later. Breaks link between strategy and execution.",
                  fix: 'Rule: Within 48 hours of cadence meeting, leaders must communicate key decisions to their teams. Provide template: "Here\'s what we decided and why. Here\'s what changes for you."',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                Build a rhythm around decisions and follow-through
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Set the review levels</h3>
                <p className="text-gray-600">
                  Choose when your team will revisit direction, priorities, coordination, and
                  current work.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Prepare the conversation</h3>
                <p className="text-gray-600">
                  Define the evidence, questions, and decisions each review needs before it starts.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Close the loop</h3>
                <p className="text-gray-600">
                  Record what changes, who owns the next steps, and when the team will review them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/execution-monitoring/okrs-kpis"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Target className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-blue-600">OKRs & KPIs</h3>
                <p className="text-gray-600 text-sm mb-3">Review metrics at each cadence level</p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/execution-monitoring/continuous-improvement"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <TrendingUp className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-purple-600">Continuous Improvement</h3>
                <p className="text-gray-600 text-sm mb-3">Build learning into every review cycle</p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/execution-monitoring/accountability-ownership"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Users className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-indigo-600">
                  Accountability & Ownership
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Assign clear owners for cadence execution
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/execution-monitoring/okrs-kpis"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Previous: OKRs & KPIs
              </Link>
              <Link
                to="/execution-monitoring/continuous-improvement"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next: Continuous Improvement <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">
            Set your review rhythm in StratPlan
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Record what changes, who owns the next steps, and when the team will review them. Start
            with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Set your review rhythm <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
